import { Fragment } from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { Group } from '../../lib/types';
import { countTeachers, formatGroupShort } from '../../lib/groups';

export type GroupCount = {
  checkedIn: number;
  total: number;
};

// Per-group food-allergy roster surfaced beneath each group row so the
// printed PDF doubles as a kitchen-friendly handout. `detail` is the
// free-text allergy description, or '' for legacy 'Yes' rows that never
// got a detail filled in (see flagDetail in lib/attendee-flags.ts).
// `isCheckedIn` is only meaningful in kitchen mode (where the kitchen
// staff cares whether the kid showed up for the meal); standard-mode
// callers may omit it.
export type FoodAllergyEntry = {
  firstName: string;
  lastName: string;
  detail: string;
  isCheckedIn?: boolean;
};

export type RotationCardProps = {
  title: string;
  groups: Group[];
  countsByGroupRef: Map<string, GroupCount>;
  // Optional: per-group lists of attendees with food allergies. When a
  // group has entries, we render an extra detail row directly under it
  // listing every kid + allergy detail so the printed PDF works as a
  // kitchen handout without needing to cross-reference the roster.
  faByGroupRef?: Map<string, FoodAllergyEntry[]>;
  // Kitchen-mode rendering: per-group cell becomes the head-count
  // total (checked-in kids + parsed teachers), the rotation summary
  // line reads "Total in {summaryLabel}: N", and every FA entry gets
  // a "(Checked-in)" / "(Not checked-in)" suffix after the name. The
  // FA list is unconditionally surfaced in kitchen mode.
  kitchenMode?: boolean;
  // Optional override for the kitchen-mode summary label. Defaults to
  // `title`. Useful for the Unassigned card where the title carries a
  // group count ("Unassigned (5)") that would read oddly next to the
  // head-count total ("Total in Unassigned (5): 8").
  summaryLabel?: string;
  onEdit?: () => void;
  onDelete?: () => void;
  emptyMessage?: string;
};

// Sum of (checked-in kids, total kids) across a list of groups. Used
// for the standard rotation summary line.
const sumCounts = (
  groups: Group[],
  countsByGroupRef: Map<string, GroupCount>
): GroupCount =>
  groups.reduce<GroupCount>(
    (acc, g) => {
      const c = countsByGroupRef.get(g.ref);
      if (c) {
        acc.checkedIn += c.checkedIn;
        acc.total += c.total;
      }
      return acc;
    },
    { checkedIn: 0, total: 0 }
  );

// Kitchen-mode rotation total head-count: sum of (checked-in kids +
// parsed teachers) across the rotation's groups. The breakdown isn't
// displayed — the kitchen only needs the meal head-count.
const sumKitchenTotal = (
  groups: Group[],
  countsByGroupRef: Map<string, GroupCount>
): number =>
  groups.reduce((acc, g) => {
    const c = countsByGroupRef.get(g.ref);
    return acc + (c?.checkedIn ?? 0) + countTeachers(g.teachers);
  }, 0);

const RotationCard = ({
  title,
  groups,
  countsByGroupRef,
  faByGroupRef,
  kitchenMode = false,
  summaryLabel,
  onEdit,
  onDelete,
  emptyMessage = 'No groups assigned to this rotation.',
}: RotationCardProps) => {
  const summary = kitchenMode
    ? `Total in ${summaryLabel ?? title}: ${sumKitchenTotal(groups, countsByGroupRef)}`
    : (() => {
        const t = sumCounts(groups, countsByGroupRef);
        return `Attendees (checked in / total): ${t.checkedIn} / ${t.total}`;
      })();

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: groups.length === 0 ? 1 : 2,
          }}
        >
          <Typography variant='h6'>{title}</Typography>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography variant='body2' color='text.secondary'>
              {summary}
            </Typography>
            {(onEdit || onDelete) && (
              <Box
                data-print-hide='true'
                sx={{ display: 'flex', gap: 0.5 }}
              >
                {onEdit && (
                  <Tooltip title='Edit rotation' placement='top'>
                    <IconButton
                      size='small'
                      aria-label={`Edit ${title}`}
                      onClick={onEdit}
                    >
                      <Edit fontSize='small' />
                    </IconButton>
                  </Tooltip>
                )}
                {onDelete && (
                  <Tooltip title='Delete rotation' placement='top'>
                    <IconButton
                      size='small'
                      aria-label={`Delete ${title}`}
                      onClick={onDelete}
                    >
                      <Delete fontSize='small' />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Stack>
        </Box>
        {groups.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            {emptyMessage}
          </Typography>
        ) : (
          <Table
            size='small'
            sx={{ '& tbody tr:last-child td': { borderBottom: 0 } }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '1%', whiteSpace: 'nowrap' }}>
                  #
                </TableCell>
                <TableCell>Group</TableCell>
                <TableCell>Teachers</TableCell>
                <TableCell align='right'>
                  {kitchenMode
                    ? 'Total (kids + teachers)'
                    : 'Checked in / Total'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((g, idx) => {
                const c = countsByGroupRef.get(g.ref) || {
                  checkedIn: 0,
                  total: 0,
                };
                const faEntries = faByGroupRef?.get(g.ref) || [];
                const teacherCount = countTeachers(g.teachers);
                return (
                  <Fragment key={g.ref}>
                    <TableRow
                      sx={
                        faEntries.length > 0
                          ? { '& > td': { borderBottom: 0 } }
                          : undefined
                      }
                    >
                      <TableCell
                        sx={{
                          width: '1%',
                          whiteSpace: 'nowrap',
                          color: 'text.secondary',
                        }}
                      >
                        {idx + 1}
                      </TableCell>
                      <TableCell>{formatGroupShort(g)}</TableCell>
                      <TableCell>{g.teachers}</TableCell>
                      <TableCell align='right'>
                        {kitchenMode
                          ? c.checkedIn + teacherCount
                          : `${c.checkedIn} / ${c.total}`}
                      </TableCell>
                    </TableRow>
                    {faEntries.length > 0 && (
                      <TableRow>
                        <TableCell />
                        <TableCell
                          colSpan={3}
                          sx={{
                            pt: 0,
                            pb: 1,
                            // Keep the list visually tied to the group
                            // row above it across a page break.
                            breakInside: 'avoid',
                            pageBreakInside: 'avoid',
                          }}
                        >
                          <Typography
                            variant='caption'
                            sx={{
                              fontStyle: 'italic',
                              fontWeight: 600,
                              display: 'block',
                              mb: 0.25,
                            }}
                          >
                            Food allergies ({faEntries.length}):
                          </Typography>
                          {faEntries.map((e, i) => {
                            // In kitchen mode each entry carries an
                            // explicit check-in flag; we surface it
                            // after the name so kitchen staff can scan
                            // by who's actually present.
                            const checkInSuffix =
                              kitchenMode && e.isCheckedIn !== undefined
                                ? e.isCheckedIn
                                  ? ' (Checked-in)'
                                  : ' (Not checked-in)'
                                : '';
                            return (
                              <Typography
                                key={i}
                                variant='caption'
                                sx={{
                                  display: 'block',
                                  pl: 1.5,
                                  whiteSpace: 'pre-wrap',
                                }}
                              >
                                • {e.firstName} {e.lastName}
                                {checkInSuffix} —{' '}
                                {e.detail || '(no details on file)'}
                              </Typography>
                            );
                          })}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default RotationCard;
